import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  Button,
  Box,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Shield as ShieldIcon,
  FilterList as FilterListIcon
} from "@mui/icons-material";
import { Icon } from "@iconify/react";
import Page from "../components/Page";
import { getRoles, getPostTypes, toggleRoleStatus } from "../api/coordinatorApi";
import { notification } from "../utils/messages";
import RoleFormDrawer from "../components/RoleFormDrawer";
import RoleDetailDrawer from "../components/RoleDetailDrawer";
import CustomLoader from "../commonComponents/CustomLoader";

export default function RolesListPage() {
  const [roles, setRoles] = useState([]);
  const [postTypes, setPostTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [filterPostType, setFilterPostType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [roleToView, setRoleToView] = useState(null);

  const fetchPostTypes = async () => {
    try {
      const response = await getPostTypes();
      if (response.status === 1) {
        setPostTypes(response.output || []);
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to load post types" })();
    }
  };

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(filterPostType && { post_type_id: filterPostType }),
        ...(filterStatus !== "" && { is_active: filterStatus }),
        ...(search && { search })
      };
      const response = await getRoles(params);
      if (response.status === 1) {
        setRoles(response.output?.data || []);
        setTotalCount(response.output?.total || 0);
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to load roles" })();
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterPostType, filterStatus, search]);

  useEffect(() => {
    fetchPostTypes();
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleMenuOpen = (event, role) => {
    setAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRole(null);
  };

  const handleEdit = () => {
    setRoleToEdit(selectedRole);
    setEditMode(true);
    setFormDrawerOpen(true);
    handleMenuClose();
  };

  const handleViewDetail = () => {
    setRoleToView(selectedRole);
    setDetailDrawerOpen(true);
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    if (!selectedRole) return;
    try {
      const newStatus = selectedRole.is_active === 1 ? 0 : 1;
      const response = await toggleRoleStatus({
        id: selectedRole.id,
        is_active: newStatus
      });
      if (response.status === 1) {
        if (newStatus === 0 && response.output?.removed_coordinators > 0) {
          notification({
            type: "success",
            message: `Role deactivated. ${response.output.removed_coordinators} coordinator(s) removed.`
          })();
        } else {
          notification({
            type: "success",
            message: response.message || "Role status updated"
          })();
        }
        fetchRoles();
      } else {
        notification({ type: "error", message: response.message || "Failed to update status" })();
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to update role status" })();
    }
    handleMenuClose();
  };

  const handleAddNew = () => {
    setRoleToEdit(null);
    setEditMode(false);
    setFormDrawerOpen(true);
  };

  const handleFormSuccess = () => {
    setFormDrawerOpen(false);
    fetchRoles();
  };

  const handleEditFromDetail = (detail) => {
    setRoleToEdit(detail);
    setEditMode(true);
    setFormDrawerOpen(true);
  };

  const getPostTypeName = (postTypeId) => {
    const pt = postTypes.find((p) => p.id === postTypeId);
    return pt?.name || "-";
  };

  return (
    <Page title="Coordinator Roles | Admin Panel">
      <Container maxWidth="xl">
        <Stack direction="column" alignItems="flex-start" spacing={2} sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: "text.primary", fontWeight: 600 }}>
            Role Management
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Define and manage system access levels and coordinator responsibilities.
          </Typography>
        </Stack>

        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
              <TextField
                size="small"
                placeholder="Search roles..."
                value={search}
                onChange={handleSearchChange}
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Post Type</InputLabel>
                <Select
                  value={filterPostType}
                  onChange={(e) => {
                    setFilterPostType(e.target.value);
                    setPage(0);
                  }}
                  label="Post Type"
                  input={<OutlinedInput label="Post Type" />}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {postTypes.map((pt) => (
                    <MenuItem key={pt.id} value={pt.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{pt.name}</span>
                        {pt.is_self === 1 && (
                          <Chip
                            label="Self"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor: "info.lighter",
                              color: "info.dark"
                            }}
                          />
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                  input={<OutlinedInput label="Status" />}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 1,
                "&:hover": { bgcolor: "primary.dark" }
              }}
            >
              Create New Role
            </Button>
          </Stack>
        </Card>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Role Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Post Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="center">
                    Position
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="center">
                    Responsibilities
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="center">
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : roles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No roles found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  roles.map((role) => (
                    <TableRow key={role.id} hover sx={{ cursor: "pointer" }} onClick={() => { setRoleToView(role); setDetailDrawerOpen(true); }}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box sx={{ p: 0.75, borderRadius: 1, bgcolor: "secondary.light", color: "secondary.main", display: "flex" }}>
                            <ShieldIcon fontSize="small" />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {role.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{role.post_type_name || getPostTypeName(role.post_type_id)}</span>
                          {role.is_self === 1 && (
                            <Chip
                              label="Self"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: "info.lighter",
                                color: "info.dark"
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={role.sort_order || "-"}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                        />
                      </TableCell>
                      <TableCell align="center">{role.responsibility_count || 0}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={role.is_active === 1 ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            bgcolor: role.is_active === 1 ? "success.light" : "grey.200",
                            color: role.is_active === 1 ? "success.dark" : "text.secondary"
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, role);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Card>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleViewDetail}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleToggleStatus}>
            <ListItemIcon>
              {selectedRole?.is_active === 1 ? (
                <ToggleOffIcon fontSize="small" sx={{ color: "error.main" }} />
              ) : (
                <ToggleOnIcon fontSize="small" sx={{ color: "success.main" }} />
              )}
            </ListItemIcon>
            <ListItemText>{selectedRole?.is_active === 1 ? "Deactivate" : "Activate"}</ListItemText>
          </MenuItem>
        </Menu>

        <RoleFormDrawer
          open={formDrawerOpen}
          onClose={() => setFormDrawerOpen(false)}
          onSuccess={handleFormSuccess}
          editMode={editMode}
          roleData={roleToEdit}
          postTypes={postTypes}
        />

        <RoleDetailDrawer
          open={detailDrawerOpen}
          onClose={() => setDetailDrawerOpen(false)}
          roleData={roleToView}
          postTypes={postTypes}
          onEdit={handleEditFromDetail}
        />
      </Container>
    </Page>
  );
}
